import { useState } from 'react'
import { Head } from '@inertiajs/react'
import { Menu, X } from 'lucide-react'
import type { SearchResult } from '@/types/search'
import type { SharedFlash } from '@/types/shared'
import { FacetHighlightChip } from '@/components/Shared/FacetHighlightChip'
import { FlashBanner } from '@/components/Shared/FlashBanner'
import { GoogleSignInForm } from '@/components/Shared/GoogleSignInForm'
import { HyrinWordmark } from '@/components/Shared/HyrinWordmark'
import { PublicFooter } from '@/components/Shared/PublicFooter'


interface EmployersLandingProps {
  engineerCount: number
  avgDaysToHire: number
  featuredCandidates: SearchResult[]
  flash?: SharedFlash
}

const COMPANY_NAMES = ['Scale AI', 'Cohere', 'Mistral', 'Perplexity', 'Together AI']

const HOW_IT_WORKS_STEPS = [
  {
    num: '01',
    title: 'Individuals prove their work.',
    body: 'Every profile is backed by verified sources: GitHub commits, arXiv papers, Hugging Face models, assessments. Not résumé bullet points.',
  },
  {
    num: '02',
    title: 'You define your needs.',
    body: 'Describe your role in plain language, like "senior RAG engineer who\'s shipped inference in production", and Hyrin parses it into verified skill filters. No boolean query wizardry.',
  },
  {
    num: '03',
    title: 'We line up your matches.',
    body: 'Matched individuals are surfaced directly in your systems. No agency cut, no recruiter inbox. Median time to first interview: 9 days.',
  },
]

const WHY_HYRIN_ITEMS = [
  {
    num: '01',
    title: 'Verified credentials, not promises.',
    body: 'Individuals come with proof: GitHub, Hugging Face, arXiv, assessments. Have a culture-fit conversation and close the hire. Save those rounds and rounds of tech discussions.',
  },
  {
    num: '02',
    title: 'No reneging on offers.',
    body: 'Two strikes and they are out of Hyrin. Individuals on this platform are serious about their next move.',
  },
  {
    num: '03',
    title: 'Pay for hire matches.',
    body: '3 out of 4 of our matches result in a hire. You only pay when you\'re ready to contact. Search is always free.',
  },
  {
    num: '04',
    title: 'Connect your agentic tools.',
    body: 'Wire Hyrin into your existing HR stack or agentic workflows. We update your systems automatically when matches are ready.',
  },
]

const PRECISION_EXAMPLES = [
  {
    id: 'checkout-scaling',
    ask: 'A scaling engineer for our e-commerce checkout. Black Friday is in ten weeks.',
    facets: ['autoscaling', '12k rpm', 'e-commerce'],
    match:
      'Held 12,000 requests a minute through last year’s peak — the autoscaling work is eight months old and sitting in their commit history.',
    source: 'GitHub',
  },
  {
    id: 'rag-production',
    ask: 'Someone to take our RAG prototype to production across 40M documents.',
    facets: ['RAG', '60M docs', 'production'],
    match:
      'Shipped retrieval over 60M documents last quarter — and published the chunking strategy they actually deployed.',
    source: 'GitHub + arXiv',
  },
  {
    id: 'inference-latency',
    ask: 'An inference engineer to get our 70B model answering under 200 ms.',
    facets: ['inference', 'p95 140 ms', 'CUDA'],
    match:
      'Cut p95 latency from 900 ms to 140 ms on a 70B deployment this spring — the kernels are public.',
    source: 'Hugging Face',
  },
]

const GLOBAL_ITEMS = [
  {
    num: '01',
    title: 'Found anywhere in the world.',
    body: 'Proof travels. A verified repo reads the same from Bengaluru, Berlin, or Buenos Aires — so your talent pool is the whole world, not a commute radius.',
  },
  {
    num: '02',
    title: 'Employment, handled.',
    body: 'Contracts, compliance, local employment law — we run the entire employment process end to end. Hiring in a new country doesn’t mean opening an entity in one.',
  },
  {
    num: '03',
    title: 'Payments, handled.',
    body: 'They’re paid on time, in their currency. You settle one invoice, in yours. No international payroll wrangling.',
  },
]

const GLOBAL_MATCHES = [
  { city: 'Bengaluru', zone: 'UTC+5:30', role: 'Inference engineer' },
  { city: 'Warsaw', zone: 'UTC+2', role: 'RAG engineer' },
  { city: 'São Paulo', zone: 'UTC−3', role: 'Scaling engineer' },
  { city: 'Lagos', zone: 'UTC+1', role: 'Data engineer' },
]

function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
}

export default function EmployersLanding({
  engineerCount,
  avgDaysToHire,
  featuredCandidates,
  flash,
}: EmployersLandingProps) {
  const formattedCount = engineerCount.toLocaleString()
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <>
      <Head title="Hire AI individuals proven by their work · Hyrin" />

      <div className="min-h-screen bg-muted">
        <FlashBanner flash={flash} />

        {/* Header */}
        <header className="relative flex h-16 items-center justify-between border-b border-border bg-muted px-5 sm:px-10">
          <a href="/" className="flex items-center gap-2">
            <HyrinWordmark />
          </a>
          <div className="flex items-center gap-5">
            <nav className="hidden items-center gap-5 md:flex">
              <a
                href="#"
                className="font-sans text-[13px] font-semibold text-foreground underline-offset-2 hover:underline"
              >
                For organizations
              </a>
              <button
                onClick={() => scrollTo('how-it-works')}
                className="font-sans text-[13px] text-muted-foreground transition-colors hover:text-foreground"
              >
                How it works?
              </button>
              <button
                onClick={() => scrollTo('why-hyrin')}
                className="font-sans text-[13px] text-muted-foreground transition-colors hover:text-foreground"
              >
                Why Hyrin?
              </button>
            </nav>
            <a
              href="/"
              className="flex h-[34px] items-center rounded-[8px] bg-foreground px-4 font-sans text-[13px] font-semibold text-background transition-opacity hover:opacity-90"
            >
              For individuals
            </a>
            <button
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              className="flex size-9 items-center justify-center rounded-[8px] border border-border text-foreground md:hidden"
            >
              {menuOpen ? <X className="size-[18px]" /> : <Menu className="size-[18px]" />}
            </button>
          </div>
          {menuOpen && (
            <nav className="absolute inset-x-0 top-16 z-20 flex flex-col border-b border-border bg-muted px-5 py-3 md:hidden">
              <a href="#" className="py-3 font-sans text-[14px] font-semibold text-foreground">
                For organizations
              </a>
              <button
                onClick={() => {
                  setMenuOpen(false)
                  scrollTo('how-it-works')
                }}
                className="py-3 text-left font-sans text-[14px] text-muted-foreground"
              >
                How it works?
              </button>
              <button
                onClick={() => {
                  setMenuOpen(false)
                  scrollTo('why-hyrin')
                }}
                className="py-3 text-left font-sans text-[14px] text-muted-foreground"
              >
                Why Hyrin?
              </button>
            </nav>
          )}
        </header>

        {/* Hero */}
        <section className="grid grid-cols-1 gap-12 px-5 pb-16 pt-12 sm:px-10 lg:grid-cols-[1fr_420px] lg:pt-[72px]">
          {/* Left */}
          <div className="flex flex-col justify-center">
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              For organizations
            </p>
            <h1 className="mb-5 font-heading text-[38px] font-bold leading-[1.04] tracking-[-0.03em] text-foreground sm:text-[46px] lg:text-[52px]">
              Hire AI individuals
              <br />
              proven by their work.
            </h1>
            <p className="mb-7 max-w-[440px] font-sans text-[17px] leading-[1.6] text-muted-foreground">
              Every individual on Hyrin is backed by evidence — real repos, papers, and models, read as
              facets, not a score. Search in plain English. Contact the ones who match.
            </p>
            <div className="mb-9 flex flex-col gap-3 sm:flex-row">
              <GoogleSignInForm iconVariant="white" className="flex h-[50px] items-center justify-center gap-2 rounded-[11px] bg-primary px-6 font-sans text-[15px] font-semibold text-primary-foreground">
                Start hiring, it&apos;s free
              </GoogleSignInForm>
              <button
                onClick={() => scrollTo('how-it-works')}
                className="h-[50px] rounded-[11px] border border-border bg-background px-6 font-sans text-[15px] font-semibold text-foreground"
              >
                See how it works
              </button>
            </div>

            {/* Stats row */}
            <div className="flex flex-col gap-5 sm:flex-row sm:gap-8">
              <div>
                <p className="font-heading text-[28px] font-bold text-foreground">
                  {formattedCount}+
                </p>
                <p className="mt-[3px] font-mono text-[11px] text-muted-foreground">
                  verified individuals
                </p>
              </div>
              <div className="sm:border-l sm:border-border sm:pl-8">
                <p className="font-heading text-[28px] font-bold text-foreground">
                  {avgDaysToHire} days
                </p>
                <p className="mt-[3px] font-mono text-[11px] text-muted-foreground">
                  median to first interview
                </p>
              </div>
              <div className="sm:border-l sm:border-border sm:pl-8">
                <p className="font-heading text-[28px] font-bold text-foreground">0</p>
                <p className="mt-[3px] font-mono text-[11px] text-muted-foreground">
                  résumés to read
                </p>
              </div>
            </div>
          </div>

          {/* Right: search mock card */}
          <div className="flex flex-col rounded-[18px] border border-border bg-background px-5 py-[22px] shadow-[0_4px_40px_rgba(20,40,32,0.08)]">
            <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              Try a search
            </p>
            <div className="mb-4 flex h-[46px] items-center gap-3 rounded-[10px] border border-border bg-muted px-4">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="shrink-0 text-muted-foreground"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <span className="font-sans text-[14px] text-muted-foreground">
                senior RAG engineer, shipped in production
              </span>
            </div>

            {/* Parsed filters */}
            <div className="mb-4 flex flex-wrap gap-[7px]">
              {['senior', 'RAG', 'production'].map((chip) => (
                <span
                  key={chip}
                  className="inline-flex items-center gap-[6px] rounded-full border border-border bg-background px-[10px] py-[5px] font-mono text-[11px] text-foreground"
                >
                  <span className="size-[5px] rounded-full bg-primary" />
                  {chip}
                </span>
              ))}
            </div>

            {/* Candidate cards */}
            <div className="flex flex-col gap-[10px]">
              {featuredCandidates.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center gap-3 rounded-[11px] border border-border p-[14px]"
                >
                  <div className="flex size-[38px] shrink-0 items-center justify-center rounded-full bg-primary font-heading text-[14px] font-bold text-primary-foreground">
                    {c.initials}
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <div className="flex items-center gap-[6px]">
                      <span className="font-sans text-[14px] font-semibold text-foreground">
                        {c.name}
                      </span>
                      {c.verified && (
                        <span className="font-mono text-[11px] font-semibold text-primary">✓</span>
                      )}
                    </div>
                    <p className="mt-[2px] truncate font-mono text-[11px] text-muted-foreground">
                      {c.matchReason}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-[4px]">
                    {c.facetHighlights[0] && <FacetHighlightChip highlight={c.facetHighlights[0]} />}
                    <span className="max-w-[140px] truncate font-mono text-[9px] uppercase tracking-[0.06em] text-muted-foreground">
                      {c.builderType}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-3 text-center font-mono text-[10px] text-muted-foreground">
              {formattedCount}+ individuals. Search is free.
            </p>
          </div>
        </section>

        {/* Logo strip */}
        <div className="border-y border-border bg-muted py-5">
          <p className="mb-4 text-center font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            Trusted by AI teams at
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 px-6">
            {COMPANY_NAMES.map((name) => (
              <span key={name} className="font-heading text-[18px] font-bold tracking-[-0.02em] text-[#C3C6C3]">
                {name}
              </span>
            ))}
          </div>
        </div>

        {/* How it works */}
        <section id="how-it-works" className="bg-background py-[72px]">
          <div className="mx-auto max-w-[1100px] px-5 sm:px-10">
            <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.22em] text-primary">
              How it works
            </p>
            <h2 className="mb-12 font-heading text-[30px] font-bold leading-[1.08] tracking-[-0.025em] text-foreground sm:text-[38px]">
              Proof, not promises.
            </h2>
            <div className="grid grid-cols-1 gap-10 md:grid-cols-3">
              {HOW_IT_WORKS_STEPS.map((step) => (
                <div key={step.num}>
                  <p className="mb-3 font-mono text-[11px] tracking-[0.08em] text-muted-foreground">
                    {step.num}
                  </p>
                  <h3 className="mb-3 font-heading text-[19px] font-bold leading-[1.25] tracking-[-0.01em] text-foreground">
                    {step.title}
                  </h3>
                  <p className="font-sans text-[15px] leading-[1.6] text-muted-foreground">
                    {step.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Precision matching */}
        <section id="precision-matching" className="bg-muted py-[72px]">
          <div className="mx-auto max-w-[1100px] px-5 sm:px-10">
            <p className="mb-3 font-mono text-[11px] uppercase tracking-eyebrow text-primary">
              Precision matching
            </p>
            <h2 className="mb-5 font-heading text-[30px] font-bold leading-[1.08] tracking-[-0.025em] text-foreground sm:text-[38px]">
              Matched on the skill, not the title.
            </h2>
            <p className="mb-12 max-w-[620px] font-sans text-[16px] leading-[1.6] text-muted-foreground">
              Hyrin reads each individual&apos;s recent work — commits, papers, models, production
              deployments — and matches your position to the precise skills it needs, weighted to
              what they&apos;ve shipped in the last year, not what a résumé claimed in 2019.
            </p>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {PRECISION_EXAMPLES.map((example) => (
                <div
                  key={example.id}
                  className="flex flex-col rounded-card border border-border bg-background p-[18px]"
                >
                  <p className="mb-2 font-mono text-[10px] uppercase tracking-eyebrow text-muted-foreground">
                    You ask
                  </p>
                  <p className="mb-4 font-sans text-[15px] font-medium leading-[1.5] text-foreground">
                    &ldquo;{example.ask}&rdquo;
                  </p>
                  <div className="mb-5 flex flex-wrap gap-[7px]">
                    {example.facets.map((facet) => (
                      <span
                        key={facet}
                        className="inline-flex items-center gap-[6px] rounded-full border border-border bg-background px-[10px] py-[5px] font-mono text-[11px] text-foreground"
                      >
                        <span className="size-[5px] rounded-full bg-primary" />
                        {facet}
                      </span>
                    ))}
                  </div>
                  <div className="mt-auto border-t border-border pt-4">
                    <p className="mb-2 font-mono text-[10px] uppercase tracking-eyebrow text-primary">
                      We match
                    </p>
                    <p className="mb-3 font-sans text-[14px] leading-[1.6] text-muted-foreground">
                      {example.match}
                    </p>
                    <span className="inline-flex rounded-full bg-secondary px-[10px] py-[4px] font-mono text-[10px] uppercase tracking-[0.08em] text-primary">
                      ✓ verified via {example.source}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Hire anywhere */}
        <section id="hire-anywhere" className="bg-background py-[72px]">
          <div className="mx-auto max-w-[1100px] px-5 sm:px-10">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_360px] lg:gap-14">
              <div>
                <p className="mb-3 font-mono text-[11px] uppercase tracking-eyebrow text-primary">
                  Hire anywhere
                </p>
                <h2 className="mb-5 font-heading text-[30px] font-bold leading-[1.08] tracking-[-0.025em] text-foreground sm:text-[38px]">
                  Your match might be nine time zones away. That&apos;s fine.
                </h2>
                <p className="mb-10 max-w-[520px] font-sans text-[16px] leading-[1.6] text-muted-foreground">
                  Precision matching doesn&apos;t stop at borders. We find the person whose recent
                  work fits your position — wherever they live — and take on everything that
                  usually makes global hiring hard.
                </p>
                <div className="flex flex-col gap-8">
                  {GLOBAL_ITEMS.map((item) => (
                    <div key={item.num} className="flex gap-5">
                      <span className="shrink-0 pt-0.5 font-mono text-[13px] font-semibold text-primary">
                        {item.num}
                      </span>
                      <div className="border-l-2 border-border pl-5">
                        <h3 className="mb-2 font-heading text-[17px] font-bold leading-snug tracking-[-0.01em] text-foreground">
                          {item.title}
                        </h3>
                        <p className="max-w-[480px] font-sans text-[14px] leading-[1.65] text-muted-foreground">
                          {item.body}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <aside className="flex h-fit flex-col rounded-card border border-border bg-background p-[18px] shadow-[0_4px_40px_rgba(20,40,32,0.08)]">
                <p className="mb-2 font-mono text-[11px] uppercase tracking-eyebrow text-muted-foreground">
                  Matched this quarter
                </p>
                <div className="flex flex-col">
                  {GLOBAL_MATCHES.map((match) => (
                    <div
                      key={match.city}
                      className="flex items-center justify-between border-b border-border py-3 last:border-b-0"
                    >
                      <div className="flex items-center gap-3">
                        <span className="size-[5px] shrink-0 rounded-full bg-primary" />
                        <div>
                          <p className="font-sans text-[14px] font-semibold text-foreground">
                            {match.city}
                          </p>
                          <p className="font-mono text-[10px] text-muted-foreground">{match.zone}</p>
                        </div>
                      </div>
                      <span className="font-mono text-[11px] text-muted-foreground">
                        {match.role}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="mt-4 border-t border-border pt-3 font-mono text-[10px] text-muted-foreground">
                  Employment &amp; payroll handled by Hyrin, everywhere.
                </p>
              </aside>
            </div>
          </div>
        </section>

        {/* Why Hyrin? */}
        <section id="why-hyrin" className="bg-muted py-[72px]">
          <div className="mx-auto max-w-[1100px] px-5 sm:px-10">
            <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.22em] text-primary">
              Why Hyrin?
            </p>
            <h2 className="mb-12 font-heading text-[30px] font-bold leading-[1.08] tracking-[-0.025em] text-foreground sm:text-[38px]">
              Built for how hiring actually works.
            </h2>
            <div className="grid grid-cols-1 gap-x-14 gap-y-10 md:grid-cols-2">
              {WHY_HYRIN_ITEMS.map((item) => (
                <div key={item.num} className="flex gap-5">
                  <span className="shrink-0 pt-0.5 font-mono text-[13px] font-semibold text-primary">
                    {item.num}
                  </span>
                  <div className="border-l-2 border-border pl-5">
                    <h3 className="mb-2 font-heading text-[17px] font-bold leading-snug tracking-[-0.01em] text-foreground">
                      {item.title}
                    </h3>
                    <p className="font-sans text-[14px] leading-[1.65] text-muted-foreground">
                      {item.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-background px-6 py-[80px] text-center">
          <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.22em] text-primary">
            Get started
          </p>
          <h2 className="mb-5 font-heading text-[32px] font-bold leading-[1.08] tracking-[-0.025em] text-foreground sm:text-[40px]">
            Find individuals who can prove it.
          </h2>
          <p className="mx-auto mb-7 max-w-[400px] font-sans text-[16px] leading-[1.55] text-muted-foreground">
            Search is free. Pay only when you&apos;re ready to contact.
          </p>
          <GoogleSignInForm iconVariant="white" className="inline-flex h-[52px] items-center gap-2 rounded-[12px] bg-primary px-8 font-sans text-[15px] font-semibold text-primary-foreground">
            Sign in with Google, it&apos;s free
          </GoogleSignInForm>
        </section>

        <PublicFooter />
      </div>
    </>
  )
}
